import { STAT_CATEGORIES_ABBR } from '@shared/constants';
import {
  AccumulatedTeamCategoriesByGameStats,
  GameID,
  ICategoryRanking,
  IMatchup,
  SeasonWeekID,
  StatCategoryAbrv,
  TeamAbrvID,
} from '@shared/types';

export enum StrengthTier {
  Elite = 'Elite',
  Good = 'Good',
  Average = 'Average',
  Poor = 'Poor',
}

export interface IStrengthOfSchedule {
  overall: number;
  offense: number;
  defense: number;
}

export interface ICategoryRankStats {
  category: StatCategoryAbrv;
  categoryLongName: string;
  currentSeasonRank: number;
  currentWeekRank: number | null;
  currentValueSeason: number;
  currentValueWeek: number | null;
  seasonAverageValue: number;
  seasonAverageRank: number;
  last3GamesValueAvg: number;
  last3GamesRankAvg: number;
  strengthTier: StrengthTier;
}

export type CategoriesStrengthStats = Record<StatCategoryAbrv, ICategoryRankStats>;

export interface IOpposingTeamStats {
  seasonWeekID: SeasonWeekID;
  gameID: GameID;
  opponentTeamAbrvID: TeamAbrvID;
  categoryStrength: CategoriesStrengthStats;
  strengthOfSchedule: IStrengthOfSchedule;
  timestamp: number;
}

/** [2025-01-ARI]: { plays NO Saints, therefore contains all the data about New Orleans  } */
export type IOpponentStrength = Record<TeamAbrvID, IOpposingTeamStats>;

export const offensiveCategories: StatCategoryAbrv[] = [
  STAT_CATEGORIES_ABBR.totalScoring2PtFor, //'2PT_F'
  STAT_CATEGORIES_ABBR.totalPassAttempts, //'ATT'
  STAT_CATEGORIES_ABBR.totalPassCompletions, //'COMP'
  STAT_CATEGORIES_ABBR.totalPassingPercentage, //'COMP_PCT'
  STAT_CATEGORIES_ABBR.totalDrives, //'DRIVES'
  STAT_CATEGORIES_ABBR.totalScoringEPFor, //'EP_F'
  STAT_CATEGORIES_ABBR.totalScoringFGFor, //'FG_F'
  STAT_CATEGORIES_ABBR.totalFumblesLost, //'FUM_LOST'
  STAT_CATEGORIES_ABBR.totalInterceptionsThrown, //'INT_THR'
  STAT_CATEGORIES_ABBR.totalPassingYards, //'PASS_YDS'
  STAT_CATEGORIES_ABBR.totalPenalties, //'PEN'
  STAT_CATEGORIES_ABBR.totalPenaltyYards, //'PEN_YDS'
  STAT_CATEGORIES_ABBR.totalPlays, //'PLAYS'
  STAT_CATEGORIES_ABBR.totalPointsScored, //'PTS_FOR'
  STAT_CATEGORIES_ABBR.totalRushingAttempts, //'RUSH_ATT'
  STAT_CATEGORIES_ABBR.totalRushingYards, //'RUSH_YDS'
  STAT_CATEGORIES_ABBR.totalSackedYardsLost, //'SACK_YDS'
  STAT_CATEGORIES_ABBR.totalSackedTimes, //'SACKED'
  STAT_CATEGORIES_ABBR.totalScoringPassingTDFor, //'TD_PASS_F'
  STAT_CATEGORIES_ABBR.totalScoringRushingTDFor, //'TD_RUSH_F'
  STAT_CATEGORIES_ABBR.totalScoringTotalTDFor, //'TD_TOT_F'
  STAT_CATEGORIES_ABBR.totalTimeOfPossession, //'TOP'
  STAT_CATEGORIES_ABBR.totalYards, //'YDS'
  STAT_CATEGORIES_ABBR.totalPassingYardsPerAttempt, //'YDS_ATT'
  STAT_CATEGORIES_ABBR.totalYardsPerPlay, //'YDS_PL'
  STAT_CATEGORIES_ABBR.totalRushingYardsPerAttempt, //'YDS_RUSH'
  STAT_CATEGORIES_ABBR.totalFirstDowns, //'1ST_DN'
  STAT_CATEGORIES_ABBR.totalFirstDownsFromPassing, //'1ST_PASS'
  STAT_CATEGORIES_ABBR.totalFirstDownsFromPenalties, //'1ST_PEN'
  STAT_CATEGORIES_ABBR.totalFirstDownsFromRushing, //'1ST_RUSH'
  STAT_CATEGORIES_ABBR.totalThirdDownAttempts, //'3RD_ATT'
  STAT_CATEGORIES_ABBR.totalThirdDownConverted, //'3RD_CONV'
  STAT_CATEGORIES_ABBR.totalThirdDownEfficiency, //'3RD_PCT'
  STAT_CATEGORIES_ABBR.totalFourthDownAttempts, //'4TH_ATT'
  STAT_CATEGORIES_ABBR.totalFourthDownEfficiency, //'4TH_PCT'
  STAT_CATEGORIES_ABBR.totalFourthDownScored, //'4TH_SCORE'
  STAT_CATEGORIES_ABBR.totalRedZoneAttempts, //'RZ_ATT'
  STAT_CATEGORIES_ABBR.totalRedZoneEfficiency, //'RZ_PCT'
  STAT_CATEGORIES_ABBR.totalRedZoneScored, //'RZ_SCORE'
];

export const defensiveCategories: StatCategoryAbrv[] = [
  STAT_CATEGORIES_ABBR.totalScoring2PtAgainst, // 2PT_A
  STAT_CATEGORIES_ABBR.totalDefensiveOrSpecialTeamsTds, // DEF_TD
  STAT_CATEGORIES_ABBR.totalScoringEPAgainst, // EP_A
  STAT_CATEGORIES_ABBR.totalScoringFGAgainst, // FG_A
  STAT_CATEGORIES_ABBR.totalForcedFumbles, // FUM_FORCE
  STAT_CATEGORIES_ABBR.totalFumblesRecovered, // FUM_REC
  STAT_CATEGORIES_ABBR.totalDefensiveInterceptions, // INT
  STAT_CATEGORIES_ABBR.totalPassingYardsAllowed, // PASS_ALLOW
  STAT_CATEGORIES_ABBR.totalPointsAllowed, // PTS_ALLOW
  STAT_CATEGORIES_ABBR.totalRushingYardsAllowed, // RUSH_ALLOW
  STAT_CATEGORIES_ABBR.totalSacks, // SACKS
  STAT_CATEGORIES_ABBR.totalSafeties, // SAFETY
  STAT_CATEGORIES_ABBR.totalScoringPassingTDAgainst, // TD_PASS_A
  STAT_CATEGORIES_ABBR.totalScoringRushingTDAgainst, // TD_RUSH_A
  STAT_CATEGORIES_ABBR.totalScoringTotalTDAgainst, // TD_TOT_A
  STAT_CATEGORIES_ABBR.totalYardsAllowed, // YDS_ALLOW
  STAT_CATEGORIES_ABBR.totalThirdDownAttemptsAgainst, // 3RD_ATT_A
  STAT_CATEGORIES_ABBR.totalThirdDownStopEfficiency, // 3RD_PCT_STOP
  STAT_CATEGORIES_ABBR.totalThirdDownStops, // 3RD_STOP
  STAT_CATEGORIES_ABBR.totalFourthDownAttemptsAgainst, // 4TH_ATT_A
  STAT_CATEGORIES_ABBR.totalFourthDownStopEfficiency, // 4TH_PCT_STOP
  STAT_CATEGORIES_ABBR.totalFourthDownStops, // 4TH_STOP
  STAT_CATEGORIES_ABBR.totalPassAttemptsAgainst, // ATT_A
  STAT_CATEGORIES_ABBR.totalPassIncompletions, // INCOMP
  STAT_CATEGORIES_ABBR.totalPassIncompletePercentage, // INCOMP_PCT
  STAT_CATEGORIES_ABBR.totalRedZoneAttemptsAgainst, // RZ_ATT_A
  STAT_CATEGORIES_ABBR.totalRedZoneStopEfficiency, // RZ_PCT_STOP
  STAT_CATEGORIES_ABBR.totalRedZoneStops, // RZ_STOP
];

export type TeamCategoryRanksType = Record<StatCategoryAbrv, ICategoryRankStats>;

export type CalculateOppositeTeamStrengthProps = {
  teamAbrvID: TeamAbrvID;
  seasonWeekID: SeasonWeekID;
  gameID: GameID;
  ranksByWeekID: ICategoryRanking[];
  cumulativeStatsBySeasonID: AccumulatedTeamCategoriesByGameStats;
  scheduleByWeekID: IMatchup[];
  teamRanks: AccumulatedTeamCategoriesByGameStats;
};
